import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkle, ArrowSquareOut, InstagramLogo, Spinner, CaretDown, CheckCircle,
} from "@phosphor-icons/react";

/**
 * StudioVisivoCard — card "Studio Visivo · render su misura".
 *
 * Soft-integration con il partner visivo (gfbvisualization.com).
 * Form interno: l'agente compila tipo servizio + briefing + telefono,
 * la richiesta arriva al partner via email. Niente menzione del nome
 * partner nella card "above the fold": resta una sezione premium.
 *
 * Props:
 *   - propertyId? string · se presente, la richiesta cita l'immobile
 *   - propertyTitle? string · usato per pre-compilare il briefing
 *   - compact? boolean · variante più piccola (es. dentro card sidebar)
 *   - testIdPrefix? string · prefisso per data-testid (default "studio-visivo")
 */
export default function StudioVisivoCard({
  propertyId,
  propertyTitle,
  compact = false,
  testIdPrefix = "studio-visivo",
}) {
  const [open, setOpen] = useState(false);
  const [serviceType, setServiceType] = useState("rendering");
  const [notes, setNotes] = useState("");
  const [phone, setPhone] = useState("");
  const [deadline, setDeadline] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e?.preventDefault();
    if (notes.trim().length < 10) {
      toast.error("Aggiungi qualche dettaglio in più nel briefing.");
      return;
    }
    setSending(true);
    try {
      await api.post("/studio-visivo/request", {
        property_id: propertyId || null,
        service_type: serviceType,
        notes: notes.trim(),
        contact_phone: phone.trim() || null,
        deadline_days: deadline ? Number(deadline) : null,
      });
      setSent(true);
      toast.success("Richiesta inviata · ti scrivono via email a breve");
    } catch (err) {
      toast.error("Invio non riuscito · riprova fra un attimo");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div
        className="rounded-xl border border-[#7C8B6E]/40 bg-[#F1F4ED] p-5"
        data-testid={`${testIdPrefix}-sent`}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#7C8B6E] flex items-center justify-center shrink-0">
            <CheckCircle size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#5A6A4D] mb-1">
              Richiesta ricevuta
            </div>
            <h3 className="font-editorial text-lg text-[#1C1C1A] leading-tight">
              Ti scrivono via email a breve.
            </h3>
            <p className="text-[12.5px] text-[#5A6A4D] mt-1.5 leading-relaxed">
              Lo studio visualizza la tua richiesta e ti risponde con un preventivo personalizzato e tempi indicativi.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border border-[#1F1F1F] bg-gradient-to-br from-[#1C1C1A] to-[#2A2A26] text-white overflow-hidden ${compact ? "" : ""}`}
      data-testid={testIdPrefix}
    >
      {/* Header sempre visibile */}
      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#C8A66B] flex items-center justify-center shrink-0">
            <Sparkle size={18} weight="fill" className="text-[#1C1C1A]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#C8A66B] mb-1">
              Studio Visivo · su richiesta
            </div>
            <h3 className="font-editorial text-[19px] sm:text-[22px] leading-tight text-white">
              Render fotorealistici, virtual staging e cover di lusso · su misura.
            </h3>
            <p className="text-[12.5px] text-white/70 mt-2 leading-relaxed max-w-xl">
              Per gli annunci premium dove serve qualcosa di più curato.
              Un partner visivo professionista prende in mano il progetto e te lo prepara su misura.
            </p>
          </div>
        </div>

        {!open && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-[#C8A66B] text-[#1C1C1A] text-[13px] font-semibold hover:bg-[#D4B47A] transition-colors min-h-[44px]"
              data-testid={`${testIdPrefix}-open`}
            >
              Richiedi preventivo
            </button>
            <a
              href="https://gfbvisualization.com"
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/15 text-white/80 hover:text-white hover:bg-white/[0.08] text-[12.5px] font-medium transition-colors min-h-[44px]"
              data-testid={`${testIdPrefix}-site`}
            >
              Vedi esempi <ArrowSquareOut size={12} weight="bold" />
            </a>
            <a
              href="https://instagram.com/gfbvisualization"
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/15 text-white/80 hover:text-white hover:bg-white/[0.08] text-[12.5px] font-medium transition-colors min-h-[44px]"
              data-testid={`${testIdPrefix}-ig`}
            >
              <InstagramLogo size={13} weight="fill" /> Instagram
            </a>
          </div>
        )}
      </div>

      {/* Form espandibile */}
      {open && (
        <form
          onSubmit={onSubmit}
          className="border-t border-white/10 px-5 sm:px-6 py-5 bg-[#0F0F0E]/40 space-y-4"
          data-testid={`${testIdPrefix}-form`}
        >
          {propertyTitle && (
            <div className="text-[11.5px] text-white/60">
              Immobile collegato: <strong className="text-white/85">{propertyTitle}</strong>
            </div>
          )}

          <div>
            <label className="block text-[11px] uppercase tracking-[0.16em] font-bold text-white/55 mb-1.5">
              Cosa ti serve
            </label>
            <div className="relative">
              <select
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                className="w-full appearance-none bg-white/[0.06] border border-white/15 text-white text-[13.5px] px-3 py-2.5 pr-10 rounded-md focus:outline-none focus:border-[#C8A66B] min-h-[44px]"
                data-testid={`${testIdPrefix}-service`}
              >
                <option value="rendering">Rendering fotorealistico</option>
                <option value="virtual_staging">Virtual staging premium (umano)</option>
                <option value="brochure_premium">Pianta arredata 2D · stile editoriale</option>
                <option value="altro">Altro · consulenza visiva</option>
              </select>
              <CaretDown size={12} weight="bold" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-white/50" />
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.16em] font-bold text-white/55 mb-1.5">
              Briefing
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Es. Mi serve un render dell'attico in Brera dopo ristrutturazione, stile contemporaneo legno-naturale, 4 viste principali..."
              className="w-full bg-white/[0.06] border border-white/15 text-white text-[13.5px] px-3 py-2.5 rounded-md focus:outline-none focus:border-[#C8A66B] placeholder:text-white/35 resize-y"
              data-testid={`${testIdPrefix}-notes`}
              minLength={10}
              maxLength={2000}
              required
            />
            <div className="text-[10.5px] text-white/45 mt-1">
              Più dettagli dai, più preciso sarà il preventivo. {notes.length}/2000
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.16em] font-bold text-white/55 mb-1.5">
                Telefono (opzionale)
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+39 333 1234567"
                className="w-full bg-white/[0.06] border border-white/15 text-white text-[13.5px] px-3 py-2.5 rounded-md focus:outline-none focus:border-[#C8A66B] placeholder:text-white/35 min-h-[44px]"
                data-testid={`${testIdPrefix}-phone`}
                maxLength={40}
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.16em] font-bold text-white/55 mb-1.5">
                Tempistica (giorni)
              </label>
              <input
                type="number"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                placeholder="es. 7"
                min={1}
                max={120}
                className="w-full bg-white/[0.06] border border-white/15 text-white text-[13.5px] px-3 py-2.5 rounded-md focus:outline-none focus:border-[#C8A66B] placeholder:text-white/35 min-h-[44px]"
                data-testid={`${testIdPrefix}-deadline`}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <button
              type="submit"
              disabled={sending || notes.trim().length < 10}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#C8A66B] text-[#1C1C1A] text-[13px] font-semibold hover:bg-[#D4B47A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
              data-testid={`${testIdPrefix}-submit`}
            >
              {sending ? <><Spinner size={14} className="animate-spin" /> Invio…</> : "Invia richiesta"}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[12.5px] text-white/65 hover:text-white px-3 py-2 min-h-[44px]"
              data-testid={`${testIdPrefix}-close`}
            >
              Annulla
            </button>
          </div>

          <p className="text-[10.5px] text-white/45 leading-snug pt-2 border-t border-white/10">
            La richiesta arriva via email al partner visivo di fiducia di Pronto. Niente impegni: ricevi un preventivo, decidi tu.
          </p>
        </form>
      )}
    </div>
  );
}
